import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import config from '../config/env';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (requestConfig: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token && requestConfig.headers) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    return requestConfig;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized - attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await axios.post(`${config.apiUrl}api/v1/reimbursement-accounts/refresh-token`, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data?.content ?? response.data;

        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        processQueue(null, accessToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);

        // Clear tokens and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        // Dispatch logout action or redirect
        window.location.href = '/login';

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Helper functions that extract content from response
export const apiPost = async <T>(
  url: string,
  body?: unknown,
  customHeaders?: Record<string, string>
): Promise<T> => {
  // For FormData, use axios directly without the default JSON Content-Type
  if (body instanceof FormData) {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post(config.apiUrl + url, body, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
        // Don't set Content-Type - axios will auto-detect with proper boundary
      },
    });
    return response.data?.content ?? response.data;
  }

  const response = await apiClient.post(url, body, {
    headers: customHeaders
      ? { ...apiClient.defaults.headers.common, ...customHeaders }
      : undefined,
  });
  return response.data?.content ?? response.data;
};

export const apiGet = async <T>(url: string): Promise<T> => {
  const response = await apiClient.get(url);
  return response.data?.content ?? response.data;
};

export const apiPut = async <T>(url: string, body?: unknown): Promise<T> => {
  const response = await apiClient.put(url, body);
  return response.data?.content ?? response.data;
};

export const apiPatch = async <T>(url: string, body?: unknown): Promise<T> => {
  const response = await apiClient.patch(url, body);
  return response.data?.content ?? response.data;
};

export const apiDelete = async <T>(url: string): Promise<T> => {
  const response = await apiClient.delete(url);
  return response.data?.content ?? response.data;
};

export default apiClient;

